import { useMemo } from 'react';
import Image from 'next/image';

import { Header } from '../components/Header';
import { PageMetadata } from '../components/PageMetadata';
import { Footer } from '../components/Footer/Footer';
import { Section, Row, Column } from '../components/Section';
import { Text } from '../components/Text';
import { CallToActionSection } from '../components/CallToActionSection';

import { GetServerPageProps } from '../lib/apollo';
import { GetStaticProps, InferGetStaticPropsType } from 'next';
import { RichText } from '../lib/copyRendering';
import { getServerPageAbout } from './about.generated';
import { logEvent } from '../lib/analytics';
import { Button } from '../components/Button';
import Link from 'next/link';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';

export default function AboutPage({
  pageData,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const page = useMemo(() => pageData.pageCollection?.items[0], [pageData]);

  const snippets = useMemo(() => page?.snippetsCollection?.items, [page]);

  const headerSnippet = useMemo(
    () =>
      snippets?.find(
        (snippet) =>
          snippet?.__typename === 'Snippet' && snippet.key === 'about-header',
      ),
    [snippets],
  );

  const videoSection = useMemo(
    () =>
      snippets?.find(
        (snippet) =>
          snippet?.__typename === 'Snippet' &&
          snippet.key === 'about-agent-benefits',
      ),
    [snippets],
  );

  const whoWeAreSnippet = useMemo(
    () =>
      snippets?.find(
        (snippet) =>
          snippet?.__typename === 'Snippet' &&
          snippet.key === 'about-who-we-are',
        // @ts-ignore
      )?.content,
    [snippets],
  );

  const ourValuesSnippet = useMemo(
    () =>
      snippets?.find(
        (snippet) =>
          snippet?.__typename === 'Snippet' &&
          snippet.key === 'about-our values',
        // @ts-ignore
      )?.content,
    [snippets],
  );

  const statsSection = useMemo(
    () =>
      snippets?.find(
        (snippet) =>
          snippet?.__typename === 'SnippetGroup' &&
          snippet.key === 'home-local-agency-stats',
      ),
    [snippets],
  );

  const codeOfConductSnippet = useMemo(
    () =>
      snippets?.find(
        (snippet) =>
          snippet?.__typename === 'Snippet' &&
          snippet.key === 'about-code-of-conduct',
        // @ts-ignore
      )?.content,
    [snippets],
  );

  const contactUsSnippet = useMemo(
    () =>
      snippets?.find(
        (snippet) =>
          snippet?.__typename === 'Snippet' &&
          snippet.key === 'about-contact-us',
        // @ts-ignore
      ),
    [snippets],
  );

  const contactUsSnippetContinued = useMemo(
    () =>
      snippets?.find(
        (snippet) =>
          snippet?.__typename === 'Snippet' &&
          snippet.key === 'about-contact-us-continued',
        // @ts-ignore
      )?.content,
    [snippets],
  );

  return (
    <>
      <PageMetadata
        pageTitle={page?.seoTitle ?? undefined}
        description={page?.seoDescription ?? undefined}
        openGraphImage={page?.openGraphImage?.url ?? undefined}
      />
      <Header />
      <Section background="BRIGHT_BLUE">
        <Row>
          <Column>
            <Text.H1 className="!text-white text-center">
              {headerSnippet?.name}
            </Text.H1>
          </Column>
        </Row>
      </Section>
      <Section background="WHITE" borderless={true}>
        <Row>
          <Column>
            <RichText content={whoWeAreSnippet} />
          </Column>
          <Column>
            <RichText content={ourValuesSnippet} />
          </Column>
        </Row>
      </Section>
      <Section background="BLUE">
        <Row>
          <Column>
            <Text.H2 className="text-center">{statsSection?.name}</Text.H2>
          </Column>
        </Row>
        <Row>
          {statsSection?.__typename === 'SnippetGroup' &&
            statsSection.snippetsCollection?.items.map(
              (snippet) =>
                snippet?.__typename === 'Snippet' && (
                  <Column
                    key={snippet.key}
                    className="text-center md:text-left"
                  >
                    <Text.H3 className="!text-base !mb-0">
                      {snippet.name}
                    </Text.H3>
                    <p className="text-3xl font-bold text-gray-900">
                      {snippet.key === 'home-stats-dedicated-agents'
                        ? pageData.agentCollection?.total
                        : snippet.content
                        ? documentToPlainTextString(snippet.content.json)
                        : snippet.key === 'home-stats-google-reviews'
                        ? pageData.reviewCollection?.total
                        : null}
                    </p>
                  </Column>
                ),
            )}
        </Row>
      </Section>
      {videoSection?.__typename === 'Snippet' && (
        <Section borderless background="WHITE" id="video">
          <Row>
            <Column>
              <Text.H2 className="text-center">{videoSection.name}</Text.H2>
            </Column>
          </Row>
          <Row className="items-center">
            <Column>
              <video
                src={videoSection.image?.url ?? undefined}
                controls
                muted
                autoPlay
                loop
                className="cursor-pointer"
                onPlay={() =>
                  logEvent({
                    category: 'About Video',
                    name: 'Play',
                  })
                }
                onPause={() =>
                  logEvent({
                    category: 'About Video',
                    name: 'Pause',
                  })
                }
                onEnded={() =>
                  logEvent({
                    category: 'About Video',
                    name: 'Ended',
                  })
                }
              />
            </Column>
            <Column>
              <RichText content={videoSection.content} />
            </Column>
          </Row>
          <Row>
            <Column>
              <Button
                variant="PRIMARY"
                size="EXTRA_LARGE"
                className="mx-auto w-full md:w-1/3"
                is={Link}
                href="/agents"
              >
                Meet our Agents
              </Button>
            </Column>
          </Row>
        </Section>
      )}
      <Section background="BLUE" id="code-of-conduct">
        <Row>
          <Column>
            <RichText content={codeOfConductSnippet} />
          </Column>
        </Row>
      </Section>
      <CallToActionSection quoteAction />
      <Section background="BLUE">
        <Row>
          <Column>
            <Text.H2>{contactUsSnippet?.name}</Text.H2>
          </Column>
        </Row>
        <Row>
          <Column>
            <RichText
              content={
                (contactUsSnippet?.__typename === 'Snippet' &&
                  contactUsSnippet.content) ||
                undefined
              }
            />
          </Column>
          <Column>
            <RichText content={contactUsSnippetContinued} />
          </Column>
        </Row>
      </Section>
      <Footer />
    </>
  );
}

export const getStaticProps: GetStaticProps<{
  pageData: GetServerPageProps<typeof getServerPageAbout>['data'];
}> = async () => {
  return {
    props: {
      pageData: (await getServerPageAbout({})).props.data,
    },
  };
};
